<script setup lang="ts">
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import { FileText, Pencil, Plus, Search, Tag, Users, X } from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'

import AddressForm, { type AddressFields } from '@/components/AddressForm.vue'
import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppPagination from '@/components/AppPagination.vue'
import InputError from '@/components/InputError.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import PortalLayout from '@/layouts/PortalLayout.vue'
import { formatLabel } from '@/lib/utils'
import { portalOrganizationPartnerPath, portalOrganizationPartnersPath, portalOrganizationRootPath } from '@/routes'

interface Organization {
  id: number
  name: string
}

interface Partner {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
  website: string | null
  partner_type: string | null
  categories: string[] | null
  address: AddressFields | null
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  active?: string | null
}

const props = defineProps<{
  organization: Organization
  stats: { partners: number; applications: number }
  partners: Partner[]
  partner_categories: Record<string, string[]>
  partner_types: string[]
  pagination: PaginationMeta
  filters: Filters
}>()

const ALL = '__all__'

const search = ref(props.filters.q ?? '')
const activeFilter = ref(props.filters.active ?? ALL)

const hasActiveFilters = computed(() => search.value !== '' || activeFilter.value !== ALL)

const filterParams = computed(() => ({
  q:      search.value || undefined,
  active: activeFilter.value !== ALL ? activeFilter.value : undefined,
}))

function applyFilters() {
  const params = Object.fromEntries(Object.entries(filterParams.value).filter(([, v]) => v != null))
  router.get(portalOrganizationRootPath(), params, { preserveState: true, replace: true })
}

let searchTimer: ReturnType<typeof setTimeout>
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})
watch(activeFilter, applyFilters)
onUnmounted(() => clearTimeout(searchTimer))

function clearFilters() {
  search.value = ''
  activeFilter.value = ALL
}

const blankAddress = () => ({ unit_no: '', street_no: '', street_name: '', street_type: '', city: '', state: '', postcode: '' })

// ── Create ────────────────────────────────────────────────────────────────────
const showCreate = ref(false)
const createForm = useForm({
  name: '', contact: '', email: '', phone: '', website: '',
  partner_type: '', categories: [] as string[],
  address_attributes: blankAddress(),
})

const createAvailableCategories = computed(() => props.partner_categories[createForm.partner_type] ?? [])

watch(
  () => createForm.partner_type,
  () => { createForm.categories = [] },
)

function toggleCreateCategory(cat: string, checked: boolean) {
  if (checked) {
    createForm.categories = [...createForm.categories, cat]
  } else {
    createForm.categories = createForm.categories.filter(c => c !== cat)
  }
}

function submitCreate() {
  createForm.post(portalOrganizationPartnersPath(), {
    onSuccess: () => { showCreate.value = false; createForm.reset() },
  })
}

// ── Edit ──────────────────────────────────────────────────────────────────────
const showEdit = ref(false)
const editingPartner = ref<Partner | null>(null)
const editForm = useForm({
  name: '', contact: '', email: '', phone: '', website: '',
  partner_type: '', categories: [] as string[],
  address_attributes: blankAddress(),
})

const editAvailableCategories = computed(() => props.partner_categories[editForm.partner_type] ?? [])

watch(
  () => editForm.partner_type,
  (type) => {
    editForm.categories = type === editingPartner.value?.partner_type
      ? [...(editingPartner.value?.categories ?? [])]
      : []
  },
)

function toggleEditCategory(cat: string, checked: boolean) {
  if (checked) {
    editForm.categories = [...editForm.categories, cat]
  } else {
    editForm.categories = editForm.categories.filter(c => c !== cat)
  }
}

function openEdit(p: Partner) {
  editingPartner.value = p
  editForm.name = p.name
  editForm.contact = p.contact
  editForm.email = p.email
  editForm.phone = p.phone
  editForm.website = p.website ?? ''
  editForm.partner_type = p.partner_type ?? ''
  editForm.categories = [...(p.categories ?? [])]
  editForm.address_attributes = {
    unit_no: p.address?.unit_no ?? '',
    street_no: p.address?.street_no ?? '',
    street_name: p.address?.street_name ?? '',
    street_type: p.address?.street_type ?? '',
    city: p.address?.city ?? '',
    state: p.address?.state ?? '',
    postcode: p.address?.postcode ?? '',
  }
  showEdit.value = true
}

function submitEdit() {
  if (!editingPartner.value) return
  editForm.patch(portalOrganizationPartnerPath(editingPartner.value.id), {
    onSuccess: () => { showEdit.value = false },
  })
}

function err(form: typeof createForm | typeof editForm, field: string): string[] | undefined {
  const msg = (form.errors as unknown as Record<string, string | undefined>)[field]
  return msg ? [msg] : undefined
}

const tableColumns: TableColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'contact', label: 'Contact' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions', align: 'right' },
]
</script>

<template>
  <Head title="Dashboard" />
  <PortalLayout actor-type="organization">
    <div class="space-y-6">
      <h1 class="text-xl font-semibold">Welcome, {{ organization.name }}</h1>

      <!-- Stats -->
      <div class="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader class="flex flex-row items-center justify-between pb-2">
            <CardTitle class="text-sm font-medium">Partners</CardTitle>
            <Users class="text-muted-foreground size-4" />
          </CardHeader>
          <CardContent>
            <p class="text-2xl font-bold">{{ stats.partners }}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader class="flex flex-row items-center justify-between pb-2">
            <CardTitle class="text-sm font-medium">Applications</CardTitle>
            <FileText class="text-muted-foreground size-4" />
          </CardHeader>
          <CardContent>
            <p class="text-2xl font-bold">{{ stats.applications }}</p>
          </CardContent>
        </Card>
      </div>

      <!-- Partners -->
      <Card>
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <Users class="text-muted-foreground size-5" />
              <div>
                <CardTitle class="text-base">Partners</CardTitle>
                <CardDescription>
                  {{ pagination.total_count }} partner{{ pagination.total_count !== 1 ? 's' : '' }}
                  {{ hasActiveFilters ? 'found' : 'total' }}
                </CardDescription>
              </div>
            </div>
            <Button size="sm" @click="showCreate = true">
              <Plus class="size-4" /> New Partner
            </Button>
          </div>
        </CardHeader>

        <!-- Filters -->
        <div class="border-border border-b px-4 pb-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div class="relative min-w-48 flex-1">
              <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input v-model="search" placeholder="Search by name or email…" class="pl-9" />
            </div>

            <Select v-model="activeFilter">
              <SelectTrigger class="w-full sm:w-36">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">Any status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Button
              v-if="hasActiveFilters"
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground w-full shrink-0 sm:w-auto"
              @click="clearFilters"
            >
              <X class="size-4" />
              Clear
            </Button>
          </div>
        </div>

        <CardContent class="p-0">
          <div v-if="partners.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <Users class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? 'No partners match your filters' : 'No partners yet' }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
            <Button v-else variant="outline" size="sm" @click="showCreate = true">
              <Plus class="size-4" />Add first partner
            </Button>
          </div>
          <template v-else>
            <AppDataTable :columns="tableColumns">
              <tr v-for="p in partners" :key="p.id" class="border-border hover:bg-muted/50 border-b last:border-0 transition-colors">
                <td class="px-6 py-4">
                  <Link :href="portalOrganizationPartnerPath(p.id)" class="font-medium hover:underline">{{ p.name }}</Link>
                </td>
                <td class="text-muted-foreground px-6 py-4">{{ p.contact }}</td>
                <td class="text-muted-foreground px-6 py-4">{{ p.email }}</td>
                <td class="px-6 py-4">
                  <Badge :variant="p.active ? 'default' : 'secondary'">{{ p.active ? 'Active' : 'Inactive' }}</Badge>
                </td>
                <td class="px-6 py-4 text-right">
                  <Button variant="ghost" size="icon" class="size-8" @click="openEdit(p)">
                    <Pencil class="size-4" /><span class="sr-only">Edit</span>
                  </Button>
                </td>
              </tr>
            </AppDataTable>
            <AppPagination
              v-if="pagination.total_pages > 1"
              :current-page="pagination.current_page"
              :total-pages="pagination.total_pages"
              :total-count="pagination.total_count"
              :per-page="pagination.per_page"
              :base-url="portalOrganizationRootPath()"
              :extra-params="filterParams"
              class="px-4"
            />
          </template>
        </CardContent>
      </Card>
    </div>

    <!-- Create dialog -->
    <AppDialog
      v-model:open="showCreate"
      title="New Partner"
      class="sm:max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <div class="grid gap-4">
        <div class="grid grid-cols-2 gap-3">
          <div class="grid gap-2 col-span-2">
            <Label>Name</Label><Input v-model="createForm.name" />
            <InputError :messages="err(createForm, 'name')" />
          </div>
          <div class="grid gap-2">
            <Label>Contact</Label><Input v-model="createForm.contact" />
            <InputError :messages="err(createForm, 'contact')" />
          </div>
          <div class="grid gap-2">
            <Label>Phone</Label><Input v-model="createForm.phone" type="tel" />
            <InputError :messages="err(createForm, 'phone')" />
          </div>
          <div class="grid gap-2 col-span-2">
            <Label>Email</Label><Input v-model="createForm.email" type="email" />
            <InputError :messages="err(createForm, 'email')" />
          </div>
          <div class="grid gap-2 col-span-2">
            <Label>Website</Label><Input v-model="createForm.website" type="url" placeholder="https://" />
          </div>
        </div>

        <div class="space-y-3 border-t pt-3">
          <div class="flex items-center gap-2">
            <Tag class="text-muted-foreground size-4" />
            <p class="text-sm font-medium">Service Type</p>
          </div>
          <div class="grid gap-2">
            <Label>Type</Label>
            <Select v-model="createForm.partner_type">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="t in partner_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="err(createForm, 'partner_type')" />
          </div>
          <div v-if="createAvailableCategories.length > 0" class="grid gap-2">
            <Label>Categories</Label>
            <div class="grid grid-cols-2 gap-x-4 gap-y-2.5">
              <div v-for="cat in createAvailableCategories" :key="cat" class="flex items-center gap-2">
                <Checkbox
                  :id="`create-cat-${cat}`"
                  :model-value="createForm.categories.includes(cat)"
                  @update:model-value="toggleCreateCategory(cat, $event as boolean)"
                />
                <Label :for="`create-cat-${cat}`" class="font-normal">{{ formatLabel(cat) }}</Label>
              </div>
            </div>
            <InputError :messages="err(createForm, 'categories')" />
          </div>
        </div>

        <p class="text-muted-foreground text-xs font-medium uppercase tracking-wide">Address</p>
        <AddressForm v-model="createForm.address_attributes" :errors="createForm.errors" error-prefix="address." />
      </div>
      <template #footer>
        <Button variant="outline" @click="showCreate = false">Cancel</Button>
        <Button :disabled="createForm.processing" @click="submitCreate">Create</Button>
      </template>
    </AppDialog>

    <!-- Edit dialog -->
    <AppDialog
      v-model:open="showEdit"
      :title="'Edit ' + (editingPartner?.name ?? 'Partner')"
      class="sm:max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <div class="grid gap-4">
        <div class="grid grid-cols-2 gap-3">
          <div class="grid gap-2 col-span-2">
            <Label>Name</Label><Input v-model="editForm.name" />
            <InputError :messages="err(editForm, 'name')" />
          </div>
          <div class="grid gap-2">
            <Label>Contact</Label><Input v-model="editForm.contact" />
          </div>
          <div class="grid gap-2">
            <Label>Phone</Label><Input v-model="editForm.phone" type="tel" />
          </div>
          <div class="grid gap-2 col-span-2">
            <Label>Email</Label><Input v-model="editForm.email" type="email" />
          </div>
          <div class="grid gap-2 col-span-2">
            <Label>Website</Label><Input v-model="editForm.website" type="url" placeholder="https://" />
          </div>
        </div>

        <div class="space-y-3 border-t pt-3">
          <div class="flex items-center gap-2">
            <Tag class="text-muted-foreground size-4" />
            <p class="text-sm font-medium">Service Type</p>
          </div>
          <div class="grid gap-2">
            <Label>Type</Label>
            <Select v-model="editForm.partner_type">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="t in partner_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="err(editForm, 'partner_type')" />
          </div>
          <div v-if="editAvailableCategories.length > 0" class="grid gap-2">
            <Label>Categories</Label>
            <div class="grid grid-cols-2 gap-x-4 gap-y-2.5">
              <div v-for="cat in editAvailableCategories" :key="cat" class="flex items-center gap-2">
                <Checkbox
                  :id="`edit-cat-${cat}`"
                  :model-value="editForm.categories.includes(cat)"
                  @update:model-value="toggleEditCategory(cat, $event as boolean)"
                />
                <Label :for="`edit-cat-${cat}`" class="font-normal">{{ formatLabel(cat) }}</Label>
              </div>
            </div>
            <InputError :messages="err(editForm, 'categories')" />
          </div>
        </div>

        <p class="text-muted-foreground text-xs font-medium uppercase tracking-wide">Address</p>
        <AddressForm v-model="editForm.address_attributes" :errors="editForm.errors" error-prefix="address." />
      </div>
      <template #footer>
        <Button variant="outline" @click="showEdit = false">Cancel</Button>
        <Button :disabled="editForm.processing" @click="submitEdit">Save</Button>
      </template>
    </AppDialog>
  </PortalLayout>
</template>
